/**
 * React Query hooks for the notification system.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { mlbApi } from '@/services/api';
import type { NotificationListResponse } from '@/services/api';
import { QUERY_RETRY_CONFIG } from '../types/statcast';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (limit?: number, offset?: number) =>
    [...notificationKeys.all, 'list', { limit, offset }] as const,
  unreadCount: () => [...notificationKeys.all, 'unreadCount'] as const,
};

export function useNotifications(limit = 30, offset = 0) {
  const { getToken, isSignedIn } = useAuth();
  return useQuery<NotificationListResponse>({
    queryKey: notificationKeys.list(limit, offset),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getNotifications(token, limit, offset);
    },
    enabled: !!isSignedIn,
    staleTime: 30 * 1000,
    ...QUERY_RETRY_CONFIG,
  });
}

export function useUnreadNotificationCount() {
  const { getToken, isSignedIn } = useAuth();
  return useQuery<{ count: number }>({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.getUnreadNotificationCount(token);
    },
    enabled: !!isSignedIn,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
    ...QUERY_RETRY_CONFIG,
  });
}

export function useMarkAllNotificationsRead() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.markAllNotificationsRead(token);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkNotificationRead() {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (notificationId: number) => {
      const token = await getToken();
      if (!token) throw new Error('Authentication required');
      return mlbApi.markNotificationRead(token, notificationId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
